import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { LockKey, Eye, EyeSlash, ArrowRight } from 'phosphor-react';

export default function ForcePasswordChangePage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const roleRedirects = {
    student: '/student-portal',
    parent: '/parent-portal',
    teacher: '/teacher-portal',
    super_admin: '/super-admin-portal',
    security_officer: '/security',
    driver: '/security/driver',
    hr: '/hr-portal',
    nurse: '/nurse-portal',
    registrar: '/registrar-portal',
    storekeeper: '/storekeeper-portal',
    canteen_staff: '/canteen-admin',
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { new_password: newPassword });
      const updated = { ...user, must_change_password: false };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      toast.success('Password updated. Welcome!');
      navigate(roleRedirects[user?.role] || '/dashboard');
    } catch (err) {
      toast.error(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F5F7F4', fontFamily: "'IBM Plex Sans', sans-serif", padding: 24
    }}>
      <div style={{
        width: '100%', maxWidth: 420, background: '#fff',
        borderRadius: 16, border: '1px solid #D8DDD6', padding: '48px 40px'
      }}>
        <div style={{
          width: 52, height: 52, background: '#EEF0EC', borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#3D5C3A', marginBottom: 24
        }}>
          <LockKey size={26} weight="duotone" />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1B2820', fontFamily: 'Manrope, sans-serif', marginBottom: 8 }}>
          Set your password
        </h1>
        <p style={{ fontSize: 14, color: '#6B8A6C', marginBottom: 32, lineHeight: 1.6 }}>
          Your account was created by an administrator. Choose a new password to continue.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {[
            { label: 'New password', value: newPassword, set: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v) },
            { label: 'Confirm password', value: confirm, set: setConfirm, show: showConfirm, toggle: () => setShowConfirm(v => !v) },
          ].map(({ label, value, set, show, toggle }) => (
            <div key={label}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1B2820', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  value={value}
                  onChange={e => set(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{
                    width: '100%', padding: '10px 42px 10px 14px', borderRadius: 8,
                    border: '1.5px solid #D8DDD6', fontSize: 14, color: '#1B2820',
                    background: '#F5F7F4', outline: 'none', boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#4E6B50'}
                  onBlur={e => e.target.style.borderColor = '#D8DDD6'}
                />
                <button
                  type="button"
                  onClick={toggle}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#6B8A6C', padding: 0
                  }}
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 24px', background: loading ? '#8AAC85' : '#3D5C3A',
              color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700,
              fontSize: 15, cursor: loading ? 'default' : 'pointer', transition: 'background 0.15s'
            }}
          >
            {loading ? 'Saving…' : 'Set password & continue'}
            {!loading && <ArrowRight size={16} weight="bold" />}
          </button>
        </form>
      </div>
    </div>
  );
}
